import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, newRecordButtonStyle, ExcelIcon, exportToExcel,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge } from './ui';
import {
  C360Table, ColumnChooser,
  ViewIcon, EditIcon, DeleteIcon,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';

// Kayıt görüntüleme/düzenleme/oluşturma artık tam-sayfa detaya (/coolers/:id, /coolers/new)
// gider — bu ekran liste-only. CRUD POST/PUT/DELETE detay sayfasında; burada sadece silme onayı.

const API_BASE = '/api/coolers';
const PAGE_SIZE = 20;

// NOTE: /api/coolers REST (OData değil) — bu yüzden useC360List kullanılamadı.
// Veri tek seferde (take=500) çekilir; filtre / sıralama / sayfalama c360 arayüzü
// ile istemci tarafında uygulanır. CRUD eski REST sözleşmesi (POST / PUT/{id} / DELETE/{id}) korunur.

const STATUS_META = {
  ACTIVE: { label: 'Aktif', tone: 'ok' },
  FAULTY: { label: 'Arızalı', tone: 'bad' },
  INACTIVE: { label: 'Pasif', tone: 'neutral' },
  REMOVED: { label: 'Sökülmüş', tone: 'neutral' },
};
const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Aktif' },
  { value: 'FAULTY', label: 'Arızalı' },
  { value: 'INACTIVE', label: 'Pasif' },
  { value: 'REMOVED', label: 'Sökülmüş' },
];

const COLUMN_FILTER_META = {
  barcode: { type: 'text', odataField: 'Barcode' },
  customerTitle: { type: 'text', odataField: 'CustomerTitle' },
  brand: { type: 'text', odataField: 'Brand' },
  doorCount: { type: 'number', odataField: 'DoorCount' },
  status: { type: 'select', odataField: 'Status', options: STATUS_OPTIONS },
};
const SEARCH_FIELDS = ['barcode', 'brand', 'model', 'customerTitle'];

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
}

function unwrapResponse(payload) {
  const data = pick(payload, 'Data', 'data') ?? payload;
  return {
    items: pick(data, 'items', 'Items') ?? [],
    activeCount: pick(data, 'activeCount', 'ActiveCount') ?? 0,
    faultyCount: pick(data, 'faultyCount', 'FaultyCount') ?? 0,
    inactiveCount: pick(data, 'inactiveCount', 'InactiveCount') ?? 0,
    removedCount: pick(data, 'removedCount', 'RemovedCount') ?? 0,
  };
}

function temperaturePill(c) {
  if (c == null || c === '') return <span style={{ color: '#94a3b8' }}>-</span>;
  const n = Number(c);
  if (!Number.isFinite(n)) return <span>-</span>;
  const ok = n >= 2 && n <= 8;
  return <StatusBadge tone={ok ? 'ok' : 'bad'}>{n.toFixed(1)}°C</StatusBadge>;
}

function formatDate(value) {
  if (!value) return '-';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return '-'; }
}

export function CoolersPage() {
  usePageMeta('Soğutucular', 'Saha noktalarındaki buzdolabı / soğutucu master kayıtları.');
  const navigate = useNavigate();
  const { canAdd, canEdit, canDelete } = usePageActions();

  const [allRows, setAllRows] = useState([]);
  // Yusuf Fix — LİSTE AÇILIŞINDA "Kayıt bulunamadı" YANIP SÖNMESİN: veri mount'taki effect'te
  // çekiliyor, yani ilk boyama fetch'ten ÖNCE olur. loading false başlayınca tablo o ilk karede
  // boş-durum metnini gösteriyor, veri gelince listeye dönüyordu — kullanıcı "önce kayıt yok dedi,
  // sonra liste geldi" diye okuyor. Yükleme durumu ilk kareden itibaren AÇIK başlar.
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [selected, setSelected] = useState(null);

  // c360 arama / filtre / sıralama / sayfalama (istemci tarafı)
  const [filterInput, setFilterInput] = useState('');
  const [filterText, setFilterText] = useState('');
  const [columnFilters, setColumnFilters] = useState({});
  const [sortField, setSortField] = useState('UpdatedDate');
  const [sortDir, setSortDir] = useState('desc');
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState(1);

  // Silme onayı (CRUD artık tam-sayfa detayda)
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 3500);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('take', '500');
      const res = await fetch(`${API_BASE}?${params.toString()}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Liste alınamadı (${res.status})`);
      const payload = await res.json();
      const data = unwrapResponse(payload);
      setAllRows(Array.isArray(data.items) ? data.items : []);
    } catch (err) {
      showToast('error', err.message || 'Liste alınamadı');
      setAllRows([]);
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const counts = useMemo(() => ({
    ACTIVE: allRows.filter((r) => pick(r, 'status', 'Status') === 'ACTIVE').length,
    FAULTY: allRows.filter((r) => pick(r, 'status', 'Status') === 'FAULTY').length,
    INACTIVE: allRows.filter((r) => pick(r, 'status', 'Status') === 'INACTIVE').length,
    REMOVED: allRows.filter((r) => pick(r, 'status', 'Status') === 'REMOVED').length,
  }), [allRows]);

  // === İstemci tarafı filtre + sıralama ===
  const filteredSorted = useMemo(() => {
    const needle = filterText.trim().toLocaleLowerCase('tr-TR');
    const matchSearch = (row) => {
      if (!needle) return true;
      return SEARCH_FIELDS.some((f) => {
        const v = pick(row, f, f.charAt(0).toUpperCase() + f.slice(1));
        return v && String(v).toLocaleLowerCase('tr-TR').includes(needle);
      });
    };
    const matchColumns = (row) => Object.entries(columnFilters).every(([colId, filter]) => {
      if (!filter) return true;
      const meta = COLUMN_FILTER_META[colId];
      const cap = colId.charAt(0).toUpperCase() + colId.slice(1);
      const raw = pick(row, colId, cap);
      if (meta?.type === 'select') {
        if (filter.value === '' || filter.value == null) return true;
        return String(raw) === String(filter.value);
      }
      if (meta?.type === 'number') {
        if (filter.value === '' || filter.value == null) return true;
        return Number(raw) === Number(filter.value);
      }
      // text
      const fv = (filter.value ?? '').toString().trim().toLocaleLowerCase('tr-TR');
      if (!fv) return true;
      return (raw ?? '').toString().toLocaleLowerCase('tr-TR').includes(fv);
    });

    const filtered = allRows.filter((r) => matchSearch(r) && matchColumns(r));
    const dir = sortDir === 'desc' ? -1 : 1;
    const sortKey = (row) => {
      switch (sortField) {
        case 'Barcode': return pick(row, 'barcode', 'Barcode') || '';
        case 'CustomerTitle': return pick(row, 'customerTitle', 'CustomerTitle') || '';
        case 'Status': return pick(row, 'status', 'Status') || '';
        case 'Brand': return pick(row, 'brand', 'Brand') || '';
        case 'DoorCount': return Number(pick(row, 'doorCount', 'DoorCount') ?? 0);
        case 'UpdatedDate':
        default: {
          const v = pick(row, 'updatedDate', 'UpdatedDate');
          return v ? new Date(v).getTime() : 0;
        }
      }
    };
    return [...filtered].sort((a, b) => {
      const av = sortKey(a);
      const bv = sortKey(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [allRows, filterText, columnFilters, sortField, sortDir]);

  const totalCount = filteredSorted.length;
  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredSorted.slice(start, start + pageSize);
  }, [filteredSorted, currentPage, pageSize]);

  const applyFilterText = useCallback((text) => { setFilterText(text); setCurrentPage(1); }, []);
  const setColumnFilter = useCallback((colId, next) => { setColumnFilters((p) => ({ ...p, [colId]: next })); setCurrentPage(1); }, []);
  const clearColumnFilter = useCallback((colId) => {
    setColumnFilters((p) => { const c = { ...p }; delete c[colId]; return c; });
    setCurrentPage(1);
  }, []);

  const openNew = () => navigate('/coolers/new');
  const openDetail = (row = selected) => { if (!row) return; navigate(`/coolers/${pick(row, 'id', 'Id')}`, { state: { row } }); };
  // #282: kalem (Değiştir) düzenleme moduna açar
  const openEditRow = (row = selected) => { if (!row) return; navigate(`/coolers/${pick(row, 'id', 'Id')}?edit=1`, { state: { row } }); };

  const handleDelete = async () => {
    if (!selected) return;
    const id = pick(selected, 'id', 'Id');
    setDeleteBusy(true);
    try {
      const res = await fetch(`${API_BASE}/${id}`, { method: 'DELETE', headers: authHeaders() });
      const payload = await res.json().catch(() => null);
      if (!res.ok) {
        const msg = pick(payload, 'Message', 'message') || `Silme başarısız (${res.status})`;
        throw new Error(msg);
      }
      showToast('success', 'Cooler silindi.');
      setDeleteConfirm(false);
      setSelected(null);
      fetchData();
    } catch (err) {
      showToast('error', err.message || 'Silme başarısız');
      setDeleteConfirm(false);
    } finally {
      setDeleteBusy(false);
    }
  };

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('barcode', {
      header: 'Barkod', meta: { filterType: 'text' },
      cell: ({ row }) => {
        const bc = pick(row.original, 'barcode', 'Barcode');
        const tid = pick(row.original, 'textId', 'TextId');
        return (
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
            {bc ? <span className="c360-erp-chip">{bc}</span> : '-'}
            {tid && <span style={{ fontSize: 11, color: 'var(--text-secondary, #6b7280)' }}>{tid}</span>}
          </div>
        );
      },
    }),
    columnHelper.accessor('customerTitle', {
      header: 'Nokta', meta: { filterType: 'text' },
      cell: ({ row }) => pick(row.original, 'customerTitle', 'CustomerTitle') || '(Müşteri yok)',
    }),
    columnHelper.accessor('brand', {
      header: 'Marka / Model', meta: { filterType: 'text' },
      cell: ({ row }) => {
        const brand = pick(row.original, 'brand', 'Brand');
        const model = pick(row.original, 'model', 'Model');
        return (
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
            <span style={{ fontWeight: 600 }}>{brand || '-'}</span>
            {model && <span style={{ fontSize: 11, color: 'var(--text-secondary, #6b7280)' }}>{model}</span>}
          </div>
        );
      },
    }),
    columnHelper.accessor('doorCount', {
      header: 'Kapı', meta: { filterType: 'number' },
      cell: ({ row }) => pick(row.original, 'doorCount', 'DoorCount') ?? 0,
    }),
    columnHelper.accessor('temperatureCelsius', {
      header: 'Sıcaklık', enableColumnFilter: false,
      cell: ({ row }) => temperaturePill(pick(row.original, 'temperatureCelsius', 'TemperatureCelsius')),
    }),
    columnHelper.accessor('lastServiceAt', {
      header: 'Son Servis', enableColumnFilter: false,
      cell: ({ row }) => formatDate(pick(row.original, 'lastServiceAt', 'LastServiceAt')),
    }),
    columnHelper.accessor('status', {
      header: 'Durum', meta: { filterType: 'select' },
      cell: ({ row }) => {
        const st = pick(row.original, 'status', 'Status');
        const meta = STATUS_META[st] || { label: st || '-', tone: 'neutral' };
        return <StatusBadge tone={meta.tone}>{meta.label}</StatusBadge>;
      },
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Görüntüle" aria-label="Görüntüle" onClick={(e) => { e.stopPropagation(); openDetail(row.original); }}><ViewIcon /></button>
          {canEdit && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--edit" title="Değiştir" aria-label="Değiştir" onClick={(e) => { e.stopPropagation(); openEditRow(row.original); }}><EditIcon /></button>}
          {canDelete && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--delete" title="Sil" aria-label="Sil" onClick={(e) => { e.stopPropagation(); setSelected(row.original); setDeleteConfirm(true); }}><DeleteIcon /></button>}
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canEdit, canDelete]);

  // c360 sıralama başlıkları → istemci sortField (PascalCase OData adı kullanılır)
  const gridToOData = {
    barcode: 'Barcode',
    customerTitle: 'CustomerTitle',
    brand: 'Brand',
    doorCount: 'DoorCount',
    status: 'Status',
    lastServiceAt: 'UpdatedDate',
  };
  const odataToGrid = Object.fromEntries(Object.entries(gridToOData).map(([k, v]) => [v, k]));
  const sorting = useMemo(() => {
    const gridId = odataToGrid[sortField];
    return gridId ? [{ id: gridId, desc: sortDir === 'desc' }] : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortField, sortDir]);
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    setCurrentPage(1);
    if (!first) { setSortField('UpdatedDate'); setSortDir('desc'); return; }
    setSortField(gridToOData[first.id] || 'UpdatedDate');
    setSortDir(first.desc ? 'desc' : 'asc');
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: pagedRows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(pick(row, 'id', 'Id')),
  });

  const handleExport = () => {
    showToast('info', 'Dışa aktarılıyor...');
    try {
      const cols = [
        { field: 'barcode', headerName: 'Barkod' },
        { field: 'textId', headerName: 'Kod' },
        { headerName: 'Nokta', valueGetter: ({ row }) => pick(row, 'customerTitle', 'CustomerTitle') || '' },
        { field: 'brand', headerName: 'Marka' },
        { field: 'model', headerName: 'Model' },
        { headerName: 'Kapı', valueGetter: ({ row }) => pick(row, 'doorCount', 'DoorCount') ?? 0 },
        { headerName: 'Sıcaklık', valueGetter: ({ row }) => pick(row, 'temperatureCelsius', 'TemperatureCelsius') ?? '' },
        { headerName: 'Son Servis', valueGetter: ({ row }) => formatDate(pick(row, 'lastServiceAt', 'LastServiceAt')) },
        { headerName: 'Durum', valueGetter: ({ row }) => (STATUS_META[pick(row, 'status', 'Status')]?.label || pick(row, 'status', 'Status') || '') },
      ];
      if (filteredSorted.length === 0) { showToast('error', 'Dışa aktarılacak veri yok'); return; }
      const dateStr = new Date().toISOString().slice(0, 10);
      exportToExcel(filteredSorted, cols, `coolerlar_${dateStr}`);
      showToast('success', `${filteredSorted.length} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Barkod / marka / nokta ile filtrele..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={pagedRows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="Soğutucular"
        subtitle="Saha noktalarındaki buzdolabı / soğutucu master kayıtları. Barkod gerçek dünya tanımlayıcısı; arıza bildirimleri bu barkod üzerinden bağlanır."
        actions={canAdd && <button style={newRecordButtonStyle} onClick={openNew}>+ Yeni Cooler</button>}
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: '4px 0 10px' }}>
          <StatusBadge tone="ok">Aktif: {counts.ACTIVE}</StatusBadge>
          <StatusBadge tone="bad">Arızalı: {counts.FAULTY}</StatusBadge>
          <StatusBadge tone="neutral">Pasif: {counts.INACTIVE}</StatusBadge>
          <StatusBadge tone="neutral">Sökülmüş: {counts.REMOVED}</StatusBadge>
        </div>
        <C360Table
          table={table}
          columnFilterMeta={COLUMN_FILTER_META}
          columnFilters={columnFilters}
          onApplyFilter={setColumnFilter}
          onClearFilter={clearColumnFilter}
          loading={loading}
          selected={selected}
          onRowClick={setSelected}
          onRowDoubleClick={openDetail}
          emptyText="Kayıt bulunamadı"
        />
      </ListPageShell>

      {deleteConfirm && selected && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(false)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}><h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Cooler Sil</h3></div>
            <div style={s.modalBody}>
              <p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}>
                <strong>"{pick(selected, 'barcode', 'Barcode')}"</strong> coolerını silmek istediğinize emin misiniz?
              </p>
              <p style={{ margin: '8px 0 0', fontSize: 12, color: '#E53E3E' }}>
                Bağlı arıza bildirimleri (CoolerFaultReport) etkilenmez (string barkod ile bağlı).
              </p>
            </div>
            <div style={s.modalFooter}>
              <button style={{ ...s.btnDanger, opacity: deleteBusy ? 0.6 : 1 }} onClick={handleDelete} disabled={deleteBusy}>{deleteBusy ? 'Siliniyor...' : 'Evet, Sil'}</button>
              <button style={s.btnSecondary} onClick={() => setDeleteConfirm(false)} disabled={deleteBusy}>İptal</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default CoolersPage;
